import { useCallback, useMemo, useState } from "react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import {
  useQueryWithEndpoint,
  useMutationWithEndpoint,
} from "@hydrologiq/shared/hooks/useGraphQLClient"
import { matchesAssetClass, sourceFitsAsset } from "@hydrologiq/shared/data-connections/sourceFilterUtils"

/**
 * The data connection an asset holds, if any.
 *
 * An asset holds at most one, so this returns a single connection rather than a
 * list even though the query is plural.
 */
export function useAssetDataConnection(assetId: string | undefined) {
  const { data, loading, error, refetch } = useQueryWithEndpoint<
    gqlTypes.GetAssetDataConnectionsQuery,
    gqlTypes.GetAssetDataConnectionsQueryVariables
  >(
    gqlTypes.GetAssetDataConnectionsDocument,
    {
      variables: { assetId: assetId || "" },
      skip: !assetId,
      fetchPolicy: "cache-and-network",
    },
    "primary",
  )

  const connections = useMemo(
    () => data?.getAssetDataConnections ?? [],
    [data],
  )

  return {
    connection: connections[0] ?? null,
    loading,
    error: error?.message,
    refetch,
  }
}

/** Connectors the tenant has configured, for the add-connection dialog. */
export function useAvailableConnectors() {
  const { data, loading } = useQueryWithEndpoint<
    gqlTypes.GetAvailableConnectionsQuery,
    gqlTypes.GetAvailableConnectionsQueryVariables
  >(gqlTypes.GetAvailableConnectionsDocument, { variables: {} }, "primary")

  const connectors = useMemo(
    () => data?.getAvailableConnections ?? [],
    [data],
  )

  return { connectors, loadingConnectors: loading }
}

/** Installation IDs on a connector that nothing currently holds. */
export function useFreeInstances(monitorId: string) {
  const { data, loading } = useQueryWithEndpoint<
    gqlTypes.GetUnlinkedInstancesQuery,
    gqlTypes.GetUnlinkedInstancesQueryVariables
  >(
    gqlTypes.GetUnlinkedInstancesDocument,
    { variables: { monitorId }, skip: !monitorId },
    "primary",
  )

  const freeInstances = useMemo(
    () => data?.getUnlinkedInstances ?? [],
    [data],
  )

  return { freeInstances, loadingFree: loading }
}

export interface CreateAssetConnectionInput {
  monitorId: string
  externalId: string
  /** Set when reusing an existing installation, so it is relinked not duplicated. */
  monitoringInstanceId?: string
  /** Device model key for nested template connectors (e.g. TYCROP → "zwg"). */
  deviceModel?: string
}

/** Create the connection this asset holds. */
export function useCreateAssetConnection(assetId: string) {
  const [creating, setCreating] = useState(false)
  const [createMutation] = useMutationWithEndpoint(
    gqlTypes.CreateDataConnectionDocument,
    undefined,
    "primary",
  )

  const createConnection = useCallback(
    async (input: CreateAssetConnectionInput) => {
      setCreating(true)
      try {
        const { data, errors } = await createMutation({
          variables: { input: { ...input, assetId } },
        })
        if (errors?.length) throw new Error(errors[0].message)
        if (!data?.createDataConnection) {
          throw new Error("Failed to create the data connection")
        }
      } catch (err: any) {
        throw new Error(
          err?.graphQLErrors?.[0]?.message ||
            err?.message ||
            "Failed to create the data connection",
        )
      } finally {
        setCreating(false)
      }
    },
    [createMutation, assetId],
  )

  return { createConnection, creating }
}

/**
 * Sources discovered on the connection, and the one this asset is mapped to.
 *
 * An asset maps to exactly one source, so this is a single mapping rather than
 * the many-to-many table a system needs.
 */
export function useAssetSourceMapping(
  monitoringInstanceId: string | undefined,
  assetClass: string | null | undefined,
  assetId?: string,
) {
  const skip = !monitoringInstanceId
  const [refreshing, setRefreshing] = useState(false)

  const {
    data: sourcesData,
    loading: loadingSources,
    refetch: refetchSources,
  } = useQueryWithEndpoint<
    gqlTypes.GetDeviceSourcesQuery,
    gqlTypes.GetDeviceSourcesQueryVariables
  >(
    gqlTypes.GetDeviceSourcesDocument,
    {
      variables: assetId
        ? { monitoringInstanceId: monitoringInstanceId || "", assetIds: [assetId] }
        : { monitoringInstanceId: monitoringInstanceId || "" },
      skip,
    },
    "primary",
  )

  const {
    data: mappedData,
    loading: loadingMapped,
    refetch: refetchMapped,
  } = useQueryWithEndpoint<
    gqlTypes.GetMappedAssetsQuery,
    gqlTypes.GetMappedAssetsQueryVariables
  >(
    gqlTypes.GetMappedAssetsDocument,
    {
      variables: { monitoringInstanceId: monitoringInstanceId || "" },
      skip,
      fetchPolicy: "cache-and-network",
    },
    "primary",
  )

  const [refreshSourcesMutation] = useMutationWithEndpoint(
    gqlTypes.RefreshDeviceSourcesDocument,
    undefined,
    "primary",
  )

  const allSources = useMemo(
    () => sourcesData?.getDeviceSources?.sources ?? [],
    [sourcesData],
  )

  // Only sources that can carry this asset: core's fittingAssetIds when it
  // sent them (which tells an AC Load's source from a DC Load's), else class.
  const sources = useMemo(
    () =>
      allSources.filter((s) =>
        assetId
          ? sourceFitsAsset(s, { id: assetId, assetClass })
          : matchesAssetClass(s.compatibleClasses, assetClass),
      ),
    [allSources, assetClass, assetId],
  )

  const refetchMapping = useCallback(() => {
    refetchSources()
    refetchMapped()
  }, [refetchSources, refetchMapped])

  const refreshSources = useCallback(async (): Promise<boolean> => {
    setRefreshing(true)
    try {
      const { data, errors } = await refreshSourcesMutation({
        variables: { monitoringInstanceId: monitoringInstanceId || "" },
      })
      if (errors?.length) throw new Error(errors[0].message)
      const isStale = data?.refreshDeviceSources?.stale ?? false
      await refetchSources()
      await refetchMapped()
      return isStale
    } finally {
      setRefreshing(false)
    }
  }, [refreshSourcesMutation, monitoringInstanceId, refetchSources, refetchMapped])

  return {
    sources,
    stale: sourcesData?.getDeviceSources?.stale ?? false,
    supportsRefresh: sourcesData?.getDeviceSources?.supportsRefresh ?? false,
    discoveredAt: sourcesData?.getDeviceSources?.discoveredAt ?? null,
    connectorType: sourcesData?.getDeviceSources?.connectorType ?? "",
    refreshSources,
    refreshing,
    mapping: mappedData?.getMappedAssets?.[0] ?? null,
    loadingMapping: loadingSources || loadingMapped,
    refetchMapping,
  }
}

/** Map this asset to a discovered source, or remove that mapping. */
export function useAssetSourceActions() {
  const [busy, setBusy] = useState(false)
  const [mapMutation] = useMutationWithEndpoint(
    gqlTypes.MapAssetToSourceDocument,
    undefined,
    "primary",
  )
  const [unmapMutation] = useMutationWithEndpoint(
    gqlTypes.UnmapAssetDocument,
    undefined,
    "primary",
  )

  const run = useCallback(async (fn: () => Promise<any>, fallback: string) => {
    setBusy(true)
    try {
      const { errors } = await fn()
      if (errors?.length) throw new Error(errors[0].message)
    } catch (err: any) {
      throw new Error(
        err?.graphQLErrors?.[0]?.message || err?.message || fallback,
      )
    } finally {
      setBusy(false)
    }
  }, [])

  const mapSource = useCallback(
    (monitoringInstanceId: string, assetId: string, sourceRef: string) =>
      run(
        () =>
          mapMutation({
            variables: { input: { monitoringInstanceId, assetId, sourceRef } },
          }),
        "Failed to map the source",
      ),
    [mapMutation, run],
  )

  const unmapSource = useCallback(
    (assetIntegrationId: string) =>
      run(
        () => unmapMutation({ variables: { assetIntegrationId } }),
        "Failed to remove the mapping",
      ),
    [unmapMutation, run],
  )

  return { mapSource, unmapSource, mappingBusy: busy }
}

/** Activate or deactivate the connection. */
export function useAssetConnectionStatus() {
  const [updating, setUpdating] = useState(false)
  const [statusMutation] = useMutationWithEndpoint(
    gqlTypes.UpdateDataConnectionStatusDocument,
    undefined,
    "primary",
  )

  const setActive = useCallback(
    async (monitoringInstanceId: string, active: boolean) => {
      setUpdating(true)
      try {
        const { errors } = await statusMutation({
          variables: { input: { monitoringInstanceId, active } },
        })
        if (errors?.length) throw new Error(errors[0].message)
      } catch (err: any) {
        throw new Error(
          err?.graphQLErrors?.[0]?.message ||
            err?.message ||
            "Failed to update the connection",
        )
      } finally {
        setUpdating(false)
      }
    },
    [statusMutation],
  )

  return { setActive, updating }
}

/** Remove the connection this asset holds. */
export function useRemoveAssetConnection() {
  const [removing, setRemoving] = useState(false)
  const [removeMutation] = useMutationWithEndpoint(
    gqlTypes.RemoveDataConnectionDocument,
    undefined,
    "primary",
  )

  const removeConnection = useCallback(
    async (monitoringInstanceId: string) => {
      setRemoving(true)
      try {
        const { errors } = await removeMutation({
          variables: { monitoringInstanceId },
        })
        if (errors?.length) throw new Error(errors[0].message)
      } catch (err: any) {
        throw new Error(
          err?.graphQLErrors?.[0]?.message ||
            err?.message ||
            "Failed to remove the data connection",
        )
      } finally {
        setRemoving(false)
      }
    },
    [removeMutation],
  )

  return { removeConnection, removing }
}

export default useAssetDataConnection
